<template>
  <div>
    <div id="videoreel">
      <video class="video"
          v-if="showVideo && source"
          ref="video"
          autoplay
          nocontrols
          :muted="shouldMuteAudio"
          v-volume="1"
          :key="videoUID"
          v-on="listeners"
          crossorigin="anonymous">
          <source :src="source.src" :type="source.type">
        </video>
    </div>
    <div v-if="showQR" id="qr-code">
      <h1 class="f1 ttu">Scan the QR code to play the game</h1>
      <img v-if="qrCode" :src="qrCode" class="img" alt="QR code">
    </div>
  </div>
</template>

<script>
import { initEcho } from '@/setup/socket.js';
import { generateId } from '@/core/shared/helpers/VariousHelper';
import { InteropNotDefinedError } from '@/core/errors';

export default {
  name: 'AppVideoReel',
  props: {
    stationId: {
      required: true,
    },
  },
  data () {
    return {
      showVideo: false,
      index: -1,
      cacheVideoUrl: null,
      videoUID: generateId(),
      listeners: {
        ended: this.nextVideo,
        error: this.nextVideo,
      },
    };
  },
  directives: {
    volume: function (el, { value }) {
      el.volume = value;
    },
  },
  computed: {
    videos () {
      return this.$store.getters['entities/Video/all']();
    },
    source () {
      if (this.index < 0 || (this.videos && !this.videos.length)) return;
      return {
        src: this.cacheVideoUrl || `${this.videos[this.index].url}?t=${new Date().getTime()}`,
        type: 'video/webm',
      };
    },
    shouldMuteAudio () {
      if (!this.$store.getters.currentStation) return;
      const desktopBuildVersion = this.$store.getters.currentStation.version || '';
      return desktopBuildVersion.match(/^3\.0/);
    },
    showQR () {
      return this.$store.getters.currentStation?.experience?.type === 'kiosk' && this.$store.getters.currentStation?.kioskQrCode;
    },
    qrCode () {
      return this.$store.getters.currentStation.kioskQrCode;
    },
  },
  methods: {
    async nextVideo () {
      const videoEl = this.$refs.video;
      if (videoEl) {
        videoEl.pause();
      }
      this.showVideo = false;
      this.videoUID = generateId(); // generate a new uid to force recreation of the video element. We had issues of sounds overlapping
      this.index = (this.index + 1) % this.videos.length;
      try {
        const result = await this.$store.dispatch('interop/getCachedFilePath', { url: this.videos[this.index].url });
        this.cacheVideoUrl = result.localPath || result || null;
      } catch (e) {
        this.cacheVideoUrl = null;
      }
      this.$nextTick(() => {
        this.showVideo = true;
      });
    },
    async init () {
      this.showVideo = false;
      initEcho(this.$store);
      await this.$store.dispatch('getStation');
      await this.$store.dispatch('getStationVideos');
      this.nextVideo();
    },
  },
  created () {
    (async function initApp () {
      try {
        await this.$store.dispatch('getStationIdentity');
        this.init();
      } catch (e) {
        // DEPRECATION backward compat with <=4.2.15
        if (e instanceof InteropNotDefinedError) {
          if (this.$store.state.launcher.hasReceivedStationIdentity) {
            this.init();
          } else {
            const unsub = this.$store.watch((state) => state.launcher.hasReceivedStationIdentity, (val) => {
              if (val) {
                unsub();
                this.init();
              }
            });
          }
        } else {
          throw e;
        }
      }
    }.bind(this))();
  },
};
</script>

<style lang="postcss">
#videoreel {
  font-family: "Open Sans", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  & .video {
    width: 100vw;
    height: 100vh;
  }
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: black;
}

.wf-loading {
  display: none;
}

#qr-code {
  background-color: yellow;
  position: fixed;
  width: 100%;
  bottom: 0;
  height: 10vh;
  z-index: 1;
  background: #fff;
  display: flex;
  align-items: center;
  font-family: 'Open Sans', Helvetica, Arial, sans-serif;

  & h1 {
    text-align: center;
    width: 100%;
  }

  & .img {
    height: 100%;
    max-height: 100%;
  }
}
</style>
